import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { CatalogueGameAdmin, CatalogueReviewEditRequest, CatalogueTemplate } from 'src/app/models/bot/catalogue';
import { BotResourceService } from 'src/app/services/bot-resource.service';
import { CataloguePreviewService } from 'src/app/services/catalogue-preview.service';
import { EmbedService } from 'src/app/services/embed.service';

const NEW_GAME_VALUE = '__new__';

@Component({
  selector: 'app-templates-review',
  templateUrl: './templates-review.component.html',
  styleUrls: ['./templates-review.component.css'],
})
export class TemplatesReviewComponent implements OnInit, OnDestroy {
  readonly newGameValue = NEW_GAME_VALUE;

  items: CatalogueTemplate[] = [];
  games: CatalogueGameAdmin[] = [];
  loading = true;
  gamesLoading = true;
  selected: CatalogueTemplate | null = null;
  busyId: number | null = null;
  deletingGameId: number | null = null;
  editing = false;
  editForm: FormGroup;
  private destroyed = false;

  constructor(
    private botService: BotResourceService,
    private previewService: CataloguePreviewService,
    private embedService: EmbedService,
    private location: Location,
    private router: Router,
    private fb: FormBuilder
  ) {
    this.editForm = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(25)]],
      game_id: [null as number | string | null, [Validators.required]],
      pending_game_name: ['', [Validators.maxLength(50)]],
    });
  }

  ngOnInit(): void {
    this.reload();
    this.reloadGames();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.embedService.resetEmbed();
  }

  get isNewGameSelected(): boolean {
    return this.editForm.value.game_id === NEW_GAME_VALUE;
  }

  reload(): void {
    this.loading = true;
    this.botService.listCatalogueReview().subscribe({
      next: (response) => {
        if (this.destroyed) {
          return;
        }
        this.items = response.items;
        this.loading = false;
        if (this.items.length) {
          const keepId = this.selected?.id;
          const next = keepId != null ? this.items.find((item) => item.id === keepId) : null;
          this.select(next || this.items[0]);
        } else {
          this.selected = null;
          this.editing = false;
          this.embedService.resetEmbed();
        }
      },
      error: () => {
        this.loading = false;
        this.items = [];
      },
    });
  }

  reloadGames(): void {
    this.gamesLoading = true;
    this.botService.listCatalogueGamesAdmin().subscribe({
      next: (games) => {
        this.games = games;
        this.gamesLoading = false;
      },
      error: () => {
        this.games = [];
        this.gamesLoading = false;
      },
    });
  }

  select(item: CatalogueTemplate): void {
    this.selected = item;
    this.editing = false;
    this.patchEditForm(item);
    if (item.template) {
      this.previewService.renderPreview(item.template, item.title);
    }
  }

  startEdit(): void {
    if (!this.selected) {
      return;
    }
    this.patchEditForm(this.selected);
    this.editing = true;
  }

  cancelEdit(): void {
    if (this.selected) {
      this.patchEditForm(this.selected);
    }
    this.editing = false;
  }

  onGameSelectChange(): void {
    const pendingCtrl = this.editForm.get('pending_game_name');
    if (this.isNewGameSelected) {
      const pending = String(this.editForm.value.pending_game_name || '').trim();
      if (!pending && this.selected?.pending_game_name) {
        pendingCtrl?.setValue(this.selected.pending_game_name);
      } else if (!pending && this.selected?.game && this.selected.game_id == null) {
        pendingCtrl?.setValue(this.selected.game);
      }
      pendingCtrl?.setValidators([
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
      ]);
    } else {
      pendingCtrl?.clearValidators();
      pendingCtrl?.setValue('');
    }
    pendingCtrl?.updateValueAndValidity();
  }

  /** Apply title/game edits locally and return to view mode — no API call. */
  saveEdits(): void {
    if (!this.selected) {
      return;
    }
    this.onGameSelectChange();
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }
    const updated = this.applyFormToTemplate(this.selected);
    if (!updated) {
      this.editForm.markAllAsTouched();
      return;
    }
    this.replaceItem(updated);
    this.selected = updated;
    this.patchEditForm(updated);
    this.editing = false;
    if (updated.template) {
      this.previewService.renderPreview(updated.template, updated.title);
    }
  }

  approve(item: CatalogueTemplate): void {
    if (this.editing) {
      return;
    }
    const body = this.bodyFromTemplate(item);
    if (!body) {
      return;
    }
    this.busyId = item.id;
    this.botService.approveCatalogueTemplate(item.id, body).subscribe({
      next: () => {
        this.busyId = null;
        this.reload();
        this.reloadGames();
      },
      error: () => (this.busyId = null),
    });
  }

  deny(item: CatalogueTemplate): void {
    this.busyId = item.id;
    this.botService.denyCatalogueTemplate(item.id).subscribe({
      next: () => {
        this.busyId = null;
        this.reload();
      },
      error: () => (this.busyId = null),
    });
  }

  deleteGame(game: CatalogueGameAdmin): void {
    if (game.templates?.length) {
      return;
    }
    const confirmed = window.confirm(`Remove unused game "${game.name}" from suggestions?`);
    if (!confirmed) {
      return;
    }
    this.deletingGameId = game.id;
    this.botService.deleteCatalogueGame(game.id).subscribe({
      next: () => {
        this.deletingGameId = null;
        this.reloadGames();
      },
      error: () => (this.deletingGameId = null),
    });
  }

  goBack(): void {
    if (window.history.length > 1) {
      this.location.back();
      return;
    }
    this.router.navigate(['/templates']);
  }

  private patchEditForm(item: CatalogueTemplate): void {
    const useNewGame = item.game_id == null || !!item.pending_game_name;
    this.editForm.reset(
      {
        title: item.title,
        game_id: useNewGame ? NEW_GAME_VALUE : item.game_id,
        pending_game_name: item.pending_game_name || (useNewGame ? item.game : ''),
      },
      { emitEvent: false }
    );
    this.onGameSelectChange();
    this.editForm.markAsPristine();
  }

  private applyFormToTemplate(item: CatalogueTemplate): CatalogueTemplate | null {
    const title = String(this.editForm.value.title || '').trim();
    if (title.length < 2) {
      return null;
    }
    if (this.isNewGameSelected) {
      const pending = String(this.editForm.value.pending_game_name || '').trim();
      if (pending.length < 2) {
        return null;
      }
      return {
        ...item,
        title,
        game_id: null,
        game: pending,
        pending_game_name: pending,
      };
    }
    const gameId = Number(this.editForm.value.game_id);
    if (!Number.isFinite(gameId) || gameId <= 0) {
      return null;
    }
    const game = this.games.find((entry) => entry.id === gameId);
    return {
      ...item,
      title,
      game_id: gameId,
      game: game?.name || item.game,
      pending_game_name: '',
    };
  }

  private bodyFromTemplate(item: CatalogueTemplate): CatalogueReviewEditRequest | null {
    const title = String(item.title || '').trim();
    if (title.length < 2) {
      return null;
    }
    if (item.game_id == null || item.pending_game_name) {
      const pending = String(item.pending_game_name || item.game || '').trim();
      if (pending.length < 2) {
        return null;
      }
      return { title, game_id: null, pending_game_name: pending };
    }
    return { title, game_id: item.game_id, pending_game_name: null };
  }

  private replaceItem(updated: CatalogueTemplate): void {
    this.items = this.items.map((item) => (item.id === updated.id ? updated : item));
  }
}
